'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import useSWR from 'swr';
import Cart from '@/components/organisms/cart';
import useCart from '@/lib/hooks/useCart';
import useAccount from '@/lib/hooks/useAccount';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import usePurchaseListActions from '@/lib/hooks/usePurchaseListActions';
import useAccountRoles from '@/lib/hooks/useAccountRoles';
import useShoppingListAccess from '@/lib/hooks/useShoppingListAccess';
import { sdk } from '@/sdk';
import { TasticProps } from '@/lib/tastics/types';
import { mapLineItem } from '@/utils/mappers/map-lineitem';
import { PurchaseList } from '@/types/entity/purchase-list';
import { mapDiscountCode } from '@/utils/mappers/map-discount-code';
import { isBackorderRestricted, isLineOverAvailableStock } from '@/utils/isBackorderRestricted';
import { resolveQuoteFormUrl } from '@/utils/zendesk-quote-form-url';
import { getDeliverableEmail } from '@/utils/get-deliverable-email';
import { useShipAndLanguage } from '@/providers/ship-and-language';
import { Props } from '../../types';
import type { ProductPricesBySku } from '@/sdk/composable-commerce-b2b/types/actions/ProductActions';
import type { Money } from '@shared/types/product/Money';
import type { Tier } from '@shared/types/product/Tier';
import type { CtProductsBySku } from '@/utils/server/getCtSnapshotForSku';
import type { Product as SharedProduct } from '@shared/types/product';

type CartClientWrapperProps = TasticProps<Props> & {
  // Server-tastic pre-fetched live prices. Seeds SWR so the first paint
  // already shows fresh per-SKU values without a client-side round-trip;
  // SWR keeps the map fresh after add/remove/qty mutations change the
  // SKU set.
  initialPricesBySku?: ProductPricesBySku;
  browseProductsHref?: string;
  spareParts?: SharedProduct[];
  sparePartsCtBySku?: CtProductsBySku;
};

const CartClientWrapper = ({
  initialPricesBySku,
  browseProductsHref,
  spareParts = [],
  sparePartsCtBySku = {},
}: CartClientWrapperProps) => {
  const { account } = useAccount();
  const { selectedBusinessUnit, selectedStore } = useStoreAndBusinessUnits();
  // EWM-690 W4 — cart only mutates purchase lists (create + add); it never reads
  // the list, so use the fetch-free actions hook (no limit=500 wishlist observer).
  const { createPurchaseList, addToWishlists } = usePurchaseListActions();

  const { permissions } = useAccountRoles(selectedBusinessUnit?.key);
  // EWM-791 — the Empty-cart dialog's "save to a purchase list first" option
  // is gated the same way as every other purchase-list surface (EWM-653).
  const { canUsePurchaseLists } = useShoppingListAccess(selectedBusinessUnit?.key);
  const { selectedLocation } = useShipAndLanguage();

  const {
    cart,
    isLoading,
    addItem,
    updateItem,
    removeItem,
    clearCart,
    redeemDiscount,
    removeDiscount,
    mutateAll,
    recalculate,
  } = useCart(selectedBusinessUnit?.key, selectedStore?.key);

  // EWM-643 — recalculate once on cart-page entry (mirror of checkout's
  // EWM-740 block). The BFF recalculate hook removes lifecycle-GO
  // (removed-from-sale) lines and re-stamps the EWM-775 stock flags, honouring
  // "removed on next cart mutation or when entering either page" without
  // touching the hot app-wide getCart read. `recalculate` writes the returned
  // cart into the SWR cache, so a pruned line disappears without a refetch.
  // Fires once per mount, only for a hydrated non-empty cart.
  const recalcOnEntryRef = useRef(false);
  useEffect(() => {
    if (recalcOnEntryRef.current) return;
    if (isLoading || !cart?.cartId || (cart.lineItems?.length ?? 0) === 0) return;
    recalcOnEntryRef.current = true;
    void recalculate();
  }, [isLoading, cart?.cartId, cart?.lineItems?.length, recalculate]);

  // EWM-332 — Opportunistic cleanup of stale `cart.shippingInfo` on
  // cart-page mount. Under the "apply freight at pay" model the cart
  // should never carry shippingInfo during browsing — freight is only
  // applied server-side at the moment of order creation. Carts left
  // dirty by the previous flow (or by a checkout the customer
  // abandoned mid-flight) are stripped here so the Order Summary
  // numbers stay in sync with the new model.
  //
  // Fires once per browser session (per cart) — `didStripRef` guards
  // against re-fire when SWR refreshes the cart. Calling `mutateAll`
  // after the action invalidates every cart cache so the displayed
  // shipping line reflects the strip immediately rather than on the
  // next mutation.
  const didStripRef = useRef(false);
  useEffect(() => {
    if (didStripRef.current) return;
    if (isLoading || !cart) return;
    if (!cart.shippingInfo) return;
    didStripRef.current = true;
    void sdk.callAction({ actionName: 'cart/removeShipping' }).then((res) => {
      if (!res.isError) void mutateAll();
    });
  }, [cart, isLoading, mutateAll]);

  // Keep per-SKU prices fresh across mutations. The server tastic seeds
  // this via `initialPricesBySku` (no flicker on first paint); after the
  // user adds/removes/changes qty, useCart's SWR refetches the cart and
  // the SKU-set changes here too — SWR's key change triggers a refetch
  // so prices match the dealer's current CT Standalone Prices.
  const cartSkus = useMemo(
    () =>
      Array.from(
        new Set(
          (cart?.lineItems ?? [])
            .map((li) => li.variant?.sku)
            .filter((sku): sku is string => typeof sku === 'string' && sku.length > 0),
        ),
      ),
    [cart?.lineItems],
  );

  const livePricesKey =
    cartSkus.length > 0 && selectedBusinessUnit?.key && selectedStore?.key
      ? (['cart/livePrices', selectedBusinessUnit.key, selectedStore.key, cartSkus.join(',')] as const)
      : null;
  const { data: livePricesData } = useSWR(
    livePricesKey,
    async () => {
      const result = await sdk.composableCommerce.product.queryPrices({ skus: cartSkus });
      return result.isError ? {} : result.data;
    },
    { fallbackData: initialPricesBySku },
  );

  const livePriceBySku = useMemo<Record<string, Money | undefined>>(() => {
    const out: Record<string, Money | undefined> = {};
    if (livePricesData) {
      for (const sku of Object.keys(livePricesData)) {
        out[sku] = livePricesData[sku]?.price;
      }
    }
    return out;
  }, [livePricesData]);

  // EWM-523 — keep the bulk-pricing tiers that ship alongside each live base
  // price. Without these the cart always showed the 1+ price regardless of
  // quantity; mapLineItem resolves them (with the line's quantity) to
  // show the same tier price CT charges and the checkout page displays.
  const livePriceBreaksBySku = useMemo<Record<string, Tier[] | undefined>>(() => {
    const out: Record<string, Tier[] | undefined> = {};
    if (livePricesData) {
      for (const sku of Object.keys(livePricesData)) {
        out[sku] = livePricesData[sku]?.priceBreaks;
      }
    }
    return out;
  }, [livePricesData]);

  const mappedLineItems = useMemo(
    () =>
      (cart?.lineItems ?? []).map((product) =>
        mapLineItem(product, { discountCodes: cart?.discountCodes ?? [], livePriceBySku, livePriceBreaksBySku }),
      ),
    [cart?.lineItems, cart?.discountCodes, livePriceBySku, livePriceBreaksBySku],
  );

  // EWM-783 — the former client-side "live transaction" patch (EWM-523, the
  // "$59.95 bug" era) is GONE. It rebased line totals onto live standalone
  // prices, but treated the EX-GST live price as tax-inclusive, so under our
  // GST-on-top config every patched line got taxAmount=0 and its NET stamped
  // as gross — the Order Summary showed internally inconsistent, GST-less
  // totals whenever a carted SKU's price had changed. commercetools is the
  // single money authority now: the EWM-643 recalculate-on-entry above makes
  // CT re-capture line prices server-side (with CT's own rounding) and the
  // returned cart lands in the SWR cache, so `cart.transaction` is fresh
  // without any client-side money math. Per-line live-price DISPLAY
  // (mapLineItem's livePriceBySku overlay, EWM-523) is unaffected.

  const invalidAddressesRequirements =
    !selectedBusinessUnit?.addresses.length && !permissions.UpdateBusinessUnitDetails;

  // EWM-791 — "save my cart before emptying it": create a purchase list and
  // copy every cart line into it. Adds run sequentially on purpose — each add
  // writes the same CT ShoppingList, so parallel calls would race on the list
  // version. Returns false when the list creation or any add fails; the
  // Empty-cart dialog blocks the clear in that case so no items are lost.
  const saveCartToNewList = useCallback(
    async (name: string) => {
      const lines = (cart?.lineItems ?? []).filter((lineItem) => lineItem.variant?.sku);
      if (lines.length === 0) return false;

      const created = await createPurchaseList({ name, store: selectedStore });
      if (!created?.wishlistId) return false;

      for (const lineItem of lines) {
        const result = await addToWishlists({
          wishlistIds: [created.wishlistId],
          sku: lineItem.variant?.sku ?? '',
          count: lineItem.count ?? 1,
        });
        if (!result?.success) return false;
      }
      return true;
    },
    [cart?.lineItems, createPurchaseList, addToWishlists, selectedStore],
  );

  const addToNewWishlist = useCallback(
    async (
      { name, description, store }: Pick<PurchaseList, 'name' | 'description' | 'store'>,
      sku?: string,
      qty?: number,
    ) => {
      const result = await createPurchaseList({ name, description, store });
      addToWishlists({
        wishlistIds: [result?.wishlistId ?? ''],
        sku: sku ?? '',
        count: qty ?? 1,
      });
      return result;
    },
    [addToWishlists, createPurchaseList],
  );

  // EWM-641 — restricted customers (no back-orders / cash `payment-terms = 'p'`) can't
  // fulfil beyond stock. `outOfStockBlocked` disables Proceed; each over-stock line
  // shows a per-line error linking to the region quote form (`overStockQuoteUrl`). The
  // stepper is NOT capped (that would leak warehouse stock levels — Luke 2026-07-03);
  // the BFF `checkout` gate is the authoritative backstop.
  const restricted = isBackorderRestricted(selectedBusinessUnit);
  const outOfStockBlocked = restricted && (mappedLineItems ?? []).some(isLineOverAvailableStock);
  const overStockQuoteUrl = restricted ? resolveQuoteFormUrl(selectedLocation?.value) : undefined;

  return (
    <Cart
      {...cart}
      key={`${selectedBusinessUnit?.key}-${selectedStore?.key}`}
      browseProductsHref={browseProductsHref}
      loading={isLoading}
      transaction={cart?.transaction}
      lineItems={mappedLineItems}
      spareParts={spareParts}
      sparePartsCtBySku={sparePartsCtBySku}
      viewCartDisabled={!permissions.ViewMyCarts}
      checkoutDisabled={!permissions.CreateMyOrdersFromMyCarts || invalidAddressesRequirements}
      quoteRequestDisabled={!permissions.CreateMyQuoteRequestsFromMyCarts || invalidAddressesRequirements}
      invalidAddressesRequirements={invalidAddressesRequirements}
      creditAccountStopped={selectedBusinessUnit?.creditStatus === 'stopped'}
      outOfStockBlocked={outOfStockBlocked}
      overStockQuoteUrl={overStockQuoteUrl}
      account={{ email: getDeliverableEmail(account) ?? '' }}
      paymentMethods={[]}
      fisThreshold={selectedBusinessUnit?.fisThreshold}
      discountCodes={(cart?.discountCodes ?? []).map((code) => ({
        ...mapDiscountCode(code),
        onRemove: async () => {
          const res = await removeDiscount(code.discountCodeId ?? '');
          return !!res.cartId;
        },
      }))}
      onApplyDiscount={async (code) => {
        const res = await redeemDiscount(code);
        return { success: res.success, message: res.message };
      }}
      discountReadOnly={!permissions.UpdateMyCarts}
      // EWM-929 — hand the hook's result straight to the organism: it decides
      // whether to drop the rows, and the dialog whether to close. The old
      // void-returning wrapper is what made a refused clear look successful.
      onClear={clearCart}
      onSaveToNewList={saveCartToNewList}
      canSaveToList={canUsePurchaseLists}
      onAdd={async (sku, count) => {
        await addItem([{ sku, count }]);
      }}
      onUpdateQuantity={async (id, count) => {
        await updateItem({ id, count });
      }}
      onRemove={async (id) => {
        await removeItem(id);
      }}
      onAddToNewWishlist={addToNewWishlist}
    />
  );
};

export default CartClientWrapper;
